import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, ShieldCheck, Clock, Award, Facebook, Phone, Mail } from 'lucide-react';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { siteMeta, contactInfo } from '../data';
import ServiceCard from '../components/ServiceCard';
import HeroCarousel from '../components/HeroCarousel';
import { imageUrl } from '../lib/images';

const fallbackGalleryImages = [
  "/_assets/th-skovservice-skovarbejde-naturpleje-mors-thy-1.jpg",
  "/_assets/th-skovservice-traefaeldning-maskiner-mors-thy-2.jpg",
  "/_assets/th-skovservice-skovservice-udstyr-mors-thy-3.jpg",
  "/_assets/traefaeldning-og-topkapning-mors-thy.jpg",
  "/_assets/grenknusning-skov-naturarealer-mors-thy.jpg",
  "/_assets/rodfraesning-stubfraesning-mors-thy.jpg",
  "/_assets/tynding-og-sporlaegning-skov-mors-thy.jpg",
  "/_assets/th-skovservice-skoventreprenoer-maskine.jpg",
  "/_assets/th-skovservice-maskinpark-arbejde.jpg",
  "/_assets/th-skovservice-galleri-projekter.jpg"
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [galleryImages, setGalleryImages] = useState(fallbackGalleryImages);
  const [index, setIndex] = useState(-1);

  useEffect(() => {
    fetch('/_api/public/content/gallery')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch gallery');
        return res.json();
      })
      .then(data => {
        const images = data.flatMap(item => item.image || []);
        if (images.length > 0) {
          setGalleryImages(images);
        }
      })
      .catch((err) => {
        console.error('Error fetching gallery:', err);
      });
  }, []);

  useEffect(() => {
    fetch('/_api/public/content/services')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then(data => {
        const sorted = data.sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
        setServices(sorted.slice(0, 4));
      })
      .catch((err) => {
        console.error('Error fetching services:', err);
        // Fallback data if API is unreachable
        setServices([
          {
            id: '1',
            title: { da: 'Træfældning' },
            slug: { da: 'traefaeldning' },
            short_description: { da: 'Sikker og professionel fældning af alle typer træer, uanset placering.' },
            image: ['/_assets/traefaeldning-og-topkapning-mors-thy.jpg']
          },
          {
            id: '2',
            title: { da: 'Tynding/Sporlægning' },
            slug: { da: 'traefaeldning-tynding-sporlaegning' },
            short_description: { da: 'Professionel skovdrift med fokus på tynding og sporlægning.' },
            image: ['/_assets/tynding-og-sporlaegning-skov-mors-thy.jpg']
          },
          {
            id: '3',
            title: { da: 'Grenknusning' },
            slug: { da: 'grenknusning' },
            short_description: { da: 'Effektiv knusning af grene og kvas direkte på arealet.' },
            image: ['/_assets/grenknusning-skov-naturarealer-mors-thy.jpg']
          },
          {
            id: '4',
            title: { da: 'Rodfræsning' },
            slug: { da: 'rodfraesning' },
            short_description: { da: 'Dybdegående fræsning af rødder, så arealet er klar til ny brug.' },
            image: ['/_assets/rodfraesning-stubfraesning-mors-thy.jpg']
          }
        ]);
      });
  }, []);

  return (
    <>
      <Helmet>
        <title>Skovservice og naturpleje på Mors, Thisted og i Thy | Få tilbud</title>
        <meta name="description" content="TH Skovservice tilbyder træfældning, skovrejsning, grødeskæring og grenknusning på Mors, i Thy, Skive, Thisted og Struer. Kontakt os og få et tilbud." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <HeroCarousel />

        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-[2.2rem] md:text-7xl font-bold mb-6 tracking-tight">
              Skovservice og naturpleje <br />
              <span className="text-emerald-400">på Mors og i Thy</span>
            </h1>
            <p className="text-lg md:text-xl text-stone-200 max-w-2xl mx-auto mb-10 leading-relaxed">
              Vi tilbyder alt inden for træfældning, topkapning og skovpleje med fokus på sikkerhed, effektivitet og et flot resultat.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/kontakt" 
                className="w-full sm:w-auto px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold transition-all transform hover:scale-105 flex items-center justify-center gap-2"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/ydelser" 
                className="w-full sm:w-auto px-8 py-4 bg-white/10 hover:bg-white/20 text-white rounded-none font-bold backdrop-blur-md transition-all border border-white/30"
              >
                Se vores ydelser
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce text-white/50">
          <div className="w-6 h-10 border-2 border-white/30 rounded-none flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-none" />
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:pr-4"
            >
              <h2 className="text-2xl sm:text-3xl lg:text-[1.85rem] xl:text-[2.2rem] font-bold text-stone-900 mb-6 leading-tight whitespace-normal lg:whitespace-nowrap">
                Din lokale ekspert i{" "}
                <span className="text-emerald-700">træ og naturpleje</span>
              </h2>
              <p className="text-stone-600 mb-6 leading-relaxed text-lg">
                Hos TH Skovservice sætter vi en ære i at levere et professionelt stykke arbejde hver gang. Vi har det rette udstyr og ekspertisen til at løse både små og store opgaver inden for skovservice, træpleje og naturpleje for private haveejere, virksomheder og skovbrug.
              </p>
              <p className="text-stone-600 mb-8 leading-relaxed text-lg">
                Med base i Karby på Mors udfører vi opgaver på Mors, i Thy samt omkring Thisted, Skive og Struer. Vi hjælper blandt andet med træfældning, skovarbejde og vedligeholdelse af grønne arealer med fokus på sikkerhed og kvalitet.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  <div>
                    <p className="font-bold text-stone-900">Fuld forsikring</p>
                    <p className="text-sm text-stone-500">Vi er fuldt dækket, så du kan være tryg.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
                  <div>
                    <p className="font-bold text-stone-900">Sikkerhed først</p>
                    <p className="text-sm text-stone-500">Vi overholder alle sikkerhedskrav.</p>
                  </div>
                </div>
              </div>
              <Link to="/om-os" className="inline-flex items-center gap-2 text-emerald-700 font-bold hover:gap-3 transition-all">
                Læs mere om os <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative lg:ml-6 xl:ml-10"
            >
              <img 
                src={imageUrl("/_assets/th-skovservice-erfaren-skoventreprenoer-mors-thy.jpg", 'width=800,quality=85,format=auto')} 
                alt="TH Skovservice - Professionel skoventreprenør med base i Karby på Mors" 
                className="rounded-none shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-emerald-900 text-white p-8 rounded-none hidden md:block">
                <p className="text-4xl font-bold mb-1">100%</p>
                <p className="text-sm text-emerald-200">Kundetilfredshed</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview - Asymmetrical Grid */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-4">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.65rem] font-bold text-stone-900 tracking-tight whitespace-normal lg:whitespace-nowrap">
                Vores ydelser indenfor skovservice og naturpleje
              </h2>
              <Link to="/ydelser" className="inline-flex items-center gap-2 text-emerald-700 font-bold hover:gap-3 transition-all shrink-0">
                Se alle ydelser <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <p className="text-stone-600 text-lg max-w-3xl">
              Vi tilbyder en bred vifte af ydelser inden for skovservice, naturpleje og træpleje med fokus på sikkerhed og kvalitet. Vi hjælper blandt andet med træfældning, skovrejsning, grødeskæring og grenknusning for både private og erhverv.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-4 h-auto md:h-[700px]">
            {services.length >= 4 && (
              <>
                {/* 1. Featured Left Card (Large) */}
                <Link 
                  to={`/ydelser/${services[0].slug?.da}`} 
                  className="group relative col-span-1 md:col-span-2 md:row-span-2 h-[300px] md:h-auto overflow-hidden bg-emerald-900"
                >
                  <img
                    src={imageUrl(services[0].image?.[0], 'width=1000,quality=85,format=auto')}
                    alt={`${services[0].title?.da} på Mors og i Thy - TH Skovservice`}
                    className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-70 transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-emerald-900/20 group-hover:bg-emerald-800/80 transition-colors duration-500" />
                  
                  <div className="absolute bottom-0 left-0 p-8 md:p-12 text-white z-10 w-full">
                    <h3 className="text-2xl md:text-4xl font-bold tracking-tight mb-4">{services[0].title?.da}</h3>
                    <p className="text-emerald-50/80 mb-6 max-w-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 hidden md:block">
                      {services[0].short_description?.da}
                    </p>
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest group-hover:translate-x-2 transition-transform">
                      Læs mere her <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>

                {/* 2. Top Right Card (Wide) */}
                <Link 
                  to={`/ydelser/${services[1].slug?.da}`} 
                  className="group relative col-span-1 md:col-span-2 md:row-span-1 h-[300px] md:h-auto overflow-hidden bg-stone-900"
                >
                  <img
                    src={imageUrl(services[1].image?.[0], 'width=800,quality=85,format=auto')}
                    alt={`${services[1].title?.da} på Mors og i Thy - TH Skovservice`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:bg-emerald-800/80 transition-all duration-500" />
                  
                  <div className="absolute bottom-0 left-0 p-8 text-white z-10">
                    <h3 className="text-2xl font-bold tracking-tight">{services[1].title?.da}</h3>
                    <span className="text-xs font-bold uppercase tracking-widest mt-2 block opacity-0 group-hover:opacity-100 transition-opacity">Læs mere</span>
                  </div>
                </Link>

                {/* 3. Bottom Right Card (Square Left) */}
                <Link 
                  to={`/ydelser/${services[2].slug?.da}`} 
                  className="group relative col-span-1 md:col-span-1 md:row-span-1 h-[300px] md:h-auto overflow-hidden bg-stone-900"
                >
                  <img
                    src={imageUrl(services[2].image?.[0], 'width=600,quality=85,format=auto')}
                    alt={`${services[2].title?.da} på Mors og i Thy - TH Skovservice`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:bg-emerald-800/80 transition-all duration-500" />
                  
                  <div className="absolute bottom-0 left-0 p-6 text-white z-10">
                    <h3 className="text-2xl font-bold tracking-tight">{services[2].title?.da}</h3>
                    <span className="text-xs font-bold uppercase tracking-widest mt-1 block opacity-0 group-hover:opacity-100 transition-opacity">Læs mere</span>
                  </div>
                </Link>

                {/* 4. Bottom Right Card (Square Right) */}
                <Link 
                  to={`/ydelser/${services[3].slug?.da}`} 
                  className="group relative col-span-1 md:col-span-1 md:row-span-1 h-[300px] md:h-auto overflow-hidden bg-stone-900"
                >
                  <img
                    src={imageUrl(services[3].image?.[0], 'width=600,quality=85,format=auto')}
                    alt={`${services[3].title?.da} på Mors og i Thy - TH Skovservice`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:bg-emerald-800/80 transition-all duration-500" />
                  
                  <div className="absolute bottom-0 left-0 p-6 text-white z-10">
                    <h3 className="text-2xl font-bold tracking-tight">{services[3].title?.da}</h3>
                    <span className="text-xs font-bold uppercase tracking-widest mt-1 block opacity-0 group-hover:opacity-100 transition-opacity">Læs mere</span>
                  </div>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Service Detail Intro Section */}
      <section className="py-24 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="order-2 md:order-1">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                Sikker og effektiv træfældning
              </h2>
              <p className="text-gray-800 text-lg leading-relaxed mb-6">
                Vi hjælper med træfældning og topkapning på Mors, i Thy og de omkringliggende områder. Uanset om et træ står på en åben grund eller tæt på bygninger, vurderer vi forholdene og den mest hensigtsmæssige fremgangsmåde.
              </p>
              <h3 className="text-xl md:text-2xl font-medium text-black mb-6">
                Vi udfører blandt andet følgende opgaver
              </h3>
              <ul className="list-disc list-outside text-gray-800 space-y-2 mb-8 ml-5">
                <li>Professionel fældning af alle træsorter</li>
                <li>Sektionel nedtagning (topkapning)</li>
                <li>Bortkørsel af grenaffald og stammer</li>
                <li>Stubfræsning og rodopgravning</li>
              </ul>
              <div className="mb-10">
                <Link 
                  to="/ydelser/traefaeldning" 
                  className="text-black hover:text-emerald-700 font-medium inline-flex items-center gap-2 border-b border-black hover:border-emerald-700 transition-colors pb-1"
                >
                  Læs mere
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="pt-8 border-t border-gray-300">
                <h3 className="text-xl md:text-2xl font-bold text-black mb-3">
                  Skovservice og naturpleje til forskellige opgaver
                </h3>
                <p className="text-gray-800 text-base leading-relaxed mb-4">
                  Ud over træfældning og topkapning tilbyder TH Skovservice også en række andre ydelser inden for skovarbejde, træpleje og naturpleje. Vi hjælper blandt andet med:
                </p>
                <ul className="list-disc list-outside text-gray-800 space-y-2 mb-8 ml-5">
                  <li>
                    <Link to="/ydelser/skovrejsning-laehegn" className="font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-2">Skovrejsning og læhegn</Link> – Etablering af ny skov og læhegn.
                  </li>
                  <li>
                    <Link to="/ydelser/grenknusning" className="font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-2">Grenknusning</Link> – Knusning af grene, krat og bevoksning.
                  </li>
                  <li>
                    <Link to="/ydelser/groedeskaering" className="font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-2">Grødeskæring</Link> – Vedligeholdelse af vandløb og grøfter.
                  </li>
                  <li>
                    <Link to="/ydelser/naturpleje" className="font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-2">Naturpleje</Link> – Pleje af naturområder og grønne arealer.
                  </li>
                  <li>
                    <Link to="/ydelser/traefaeldning-tynding-sporlaegning" className="font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-2">Tynding og sporlægning</Link> – Tynding af skov og etablering af spor.
                  </li>
                  <li>
                    <Link to="/ydelser/rodfraesning" className="font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-2">Rodfræsning</Link> – Fræsning af træstubbe og rødder.
                  </li>
                  <li>
                    <Link to="/ydelser/entreprenoer" className="font-semibold text-emerald-800 hover:text-emerald-950 underline underline-offset-2">Entreprenørarbejde</Link> – Forskellige maskinopgaver og arbejde med terræn.
                  </li>
                </ul>
                <p className="text-gray-800 text-base leading-relaxed mb-6">
                  Vi tager udgangspunkt i opgavens omfang og forholdene på stedet. Har du brug for hjælp fra en skovhugger eller til vedligeholdelse af grønne arealer, er du velkommen til at kontakte os for en gratis besigtigelse og et uforpligtende tilbud.
                </p>

                <div className="flex flex-wrap gap-4 items-center not-prose pt-2">
                  <a
                    href="tel:27432314"
                    className="inline-flex items-center gap-2.5 bg-[#285c35] text-white px-6 py-3.5 rounded-md font-bold hover:bg-[#1f4729] transition-all shadow-sm hover:shadow text-base no-underline"
                  >
                    <Phone className="w-4 h-4" />
                    <span>Ring til os</span>
                  </a>
                  <a
                    href="mailto:Thorsten.hansen94@gmail.com"
                    className="inline-flex items-center gap-2.5 bg-white text-[#285c35] border-2 border-[#285c35] px-6 py-3 rounded-md font-bold hover:bg-stone-50 transition-all shadow-sm hover:shadow text-base no-underline"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Få et tilbud på mail</span>
                  </a>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2 md:sticky md:top-24">
              <img 
                src={imageUrl("/_assets/sikker-effektiv-traefaeldning-topkapning-mors-thy.jpg", 'width=800,quality=85,format=auto')} 
                alt="Sikker og effektiv træfældning og topkapning på Mors og i Thy" 
                className="w-full h-auto object-cover shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Moving Slideshow */}
      <section className="py-24 bg-gray-100 border-t border-gray-200 overflow-hidden">
        <div className="container mx-auto px-4 mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 text-center">Galleri</h2>
        </div>
        
        <div className="relative flex overflow-hidden group">
          <motion.div 
            className="flex gap-4 whitespace-nowrap"
            animate={{ x: [0, -1920] }}
            transition={{ 
              duration: 40, 
              repeat: Infinity, 
              ease: "linear" 
            }}
          >
            {[...galleryImages, ...galleryImages].map((img, i) => (
              <div 
                key={i} 
                className="w-[300px] md:w-[450px] aspect-[4/3] shrink-0 overflow-hidden cursor-pointer relative group/item"
                onClick={() => setIndex(i % galleryImages.length)}
              >
                <img 
                  src={imageUrl(img, 'width=600,quality=80,format=auto')} 
                  alt={`TH Skovservice - Træfældning og naturpleje på Mors og i Thy billede ${((i % galleryImages.length) + 1)}`} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-110"
                />
                <div className="absolute inset-0 bg-emerald-900/20 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="text-white font-bold border-b border-white">Se billede</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <Lightbox
          index={index}
          open={index >= 0}
          close={() => setIndex(-1)}
          slides={galleryImages.map(src => ({ src }))}
        />
      </section>

      {/* Social Media Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <img 
                src={imageUrl("/_assets/th-skovservice-arbejde-skov-have-mors.jpg", 'width=800,quality=85,format=auto')} 
                alt="TH Skovservice på arbejde med træpleje og skovservice" 
                className="w-full h-full object-cover shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Følg os på de sociale medier
              </h2>
              <p className="text-gray-800 leading-relaxed mb-8">
                Vi deler løbende billeder og historier fra vores hverdag i skoven og i de danske haver. Følg med for at se vores nyeste projekter, få gode råd til din have og se hvordan vi løser udfordrende opgaver.
              </p>
              <div className="flex gap-4">
                <a 
                  href={contactInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-gray-700 transition-colors"
                >
                  <Facebook className="w-8 h-8" />
                </a>
              </div>
              <div className="mt-8">
                <a 
                  href={contactInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black hover:text-gray-700 font-medium inline-flex items-center gap-2 border-b border-black hover:border-gray-700 transition-colors pb-1"
                >
                  Besøg vores profil
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={imageUrl("/_assets/th-skovservice-kontakt-tilbud-skovservice.jpg", 'width=1600,quality=85,format=auto')} 
            alt="Kontakt TH Skovservice for tilbud på professionel skovservice og naturpleje" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#004e3a]/60 to-[#004e3a]" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Kontakt os i dag for et uforpligtende tilbud
              </h2>
              <p className="text-emerald-50 leading-relaxed mb-8">
                Har du et træ der skal fældes, eller trænger din skov til pleje? Vi står klar til at hjælpe dig med professionel rådvining og et skarpt tilbud. Udfyld formularen, så vender vi tilbage hurtigst muligt.
              </p>
              <div className="space-y-4 text-emerald-50">
                <p>Telefon: {contactInfo.phone}</p>
                <p>Email: {contactInfo.email}</p>
                <p>Adresse: {contactInfo.address}</p>
              </div>
            </div>
            <div>
              <form 
                action="https://api.dev.auxo.dk/webform/submit" 
                method="POST"
                className="space-y-6"
              >
                {/* Honeypot */}
                <div className="sr-only absolute -z-10">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <input 
                      type="text" 
                      name="name" 
                      placeholder="Navn" 
                      required
                      className="w-full bg-transparent border-0 border-b border-emerald-400/50 text-white placeholder:text-emerald-200 focus:ring-0 focus:border-white transition-colors py-2"
                    />
                  </div>
                  <div>
                    <input 
                      type="tel" 
                      name="phone" 
                      placeholder="Telefon" 
                      required
                      className="w-full bg-transparent border-0 border-b border-emerald-400/50 text-white placeholder:text-emerald-200 focus:ring-0 focus:border-white transition-colors py-2"
                    />
                  </div>
                </div>
                <div>
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="E-mail" 
                    required
                    className="w-full bg-transparent border-0 border-b border-emerald-400/50 text-white placeholder:text-emerald-200 focus:ring-0 focus:border-white transition-colors py-2"
                  />
                </div>
                <div>
                  <textarea 
                    name="message" 
                    rows="4" 
                    placeholder="Besked" 
                    required
                    className="w-full bg-transparent border-0 border-b border-emerald-400/50 text-white placeholder:text-emerald-200 focus:ring-0 focus:border-white transition-colors py-2 resize-none"
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-10 rounded-none transition-all w-max shadow-lg"
                >
                  Send besked
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
